import { useAlert } from '@ads/components-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInactivateFinancial } from '@/features/individual-payment/hooks/useInactivateFinancial';
import { useIndividualPaymentPeople } from '@/features/individual-payment/hooks/useIndividualPaymentPeople';
import type { IndividualPaymentContractAction } from '@/features/individual-payment/types/individualPayment';
import { IndividualPaymentFilterBar } from '@/features/individual-payment/ui/molecules/IndividualPaymentFilterBar';
import type { ContractActionContext } from '@/features/individual-payment/ui/organisms/IndividualPaymentTable';
import { IndividualPaymentTable } from '@/features/individual-payment/ui/organisms/IndividualPaymentTable';
import { ParcelActionConfirmModal } from '@/features/individual-payment/ui/organisms/ParcelActionConfirmModal';
import { IndividualPaymentListTemplate } from '@/features/individual-payment/ui/templates/IndividualPaymentListTemplate';

export const Route = createFileRoute('/_authenticated/individual-payment/')({
  component: IndividualPaymentListPage,
});

/** Contrato escolhido para inativação, com o contexto da linha-pai. */
interface InactivateTarget {
  readonly financialPersonId: number;
  readonly context: ContractActionContext;
}

/**
 * Página de pesquisa de Pagamento Individual.
 *
 * Orquestra template, barra de filtros e tabela. A busca só acontece quando o
 * operador aciona "Pesquisar"; aqui a página apenas volta para a primeira página
 * nesse momento.
 */
export function IndividualPaymentListPage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);

  const {
    people,
    isLoading,
    isError,
    refetch,
    hasSearched,
    hasNextPage,
    hasPreviousPage,
    rangeFrom,
    rangeTo,
    totalCount,
    totalPages,
  } = useIndividualPaymentPeople(pageIndex + 1);

  /**
   * Contrato aguardando confirmação de inativação.
   *
   * Guarda o contexto junto do id porque o modal descreve o que será desfeito —
   * aluno e curso — e o hook precisa da pessoa para invalidar a linha certa.
   */
  const [inactivateTarget, setInactivateTarget] = useState<InactivateTarget | null>(null);
  const {
    inactivate,
    isPending: isInactivating,
    error: inactivateError,
    reset: resetInactivate,
  } = useInactivateFinancial(inactivateTarget?.context.personId ?? null);

  const handleContractAction = (
    action: IndividualPaymentContractAction,
    financialPersonId: number,
    context: ContractActionContext,
  ) => {
    if (action === 'deactivateFinancial') {
      // A confirmação é obrigatória: o endpoint cancela as parcelas abertas
      // junto com a inativação, e nada disso é desfeito por esta tela.
      resetInactivate();
      setInactivateTarget({ financialPersonId, context });
      return;
    }

    if (action === 'addIndividualPayment') {
      // Aberta a partir do contrato de alguém, a criação já sabe de quem é. Só a
      // pessoa vai preenchida: a matrícula do contrato existente não é
      // necessariamente a do plano novo, e supor isso escolheria pelo operador.
      void navigate({
        to: '/individual-payment/create',
        search: {
          tab: 'detalhes',
          personId: String(context.personId),
          personName: context.personName,
        },
      });
      return;
    }

    if (action === 'viewParcels') {
      void navigate({
        to: '/individual-payment/$financialPersonId/parcels',
        params: { financialPersonId: String(financialPersonId) },
      });
      return;
    }

    if (action === 'editIndividualPayment') {
      void navigate({
        to: '/individual-payment/$financialPersonId',
        params: { financialPersonId: String(financialPersonId) },
        search: { tab: 'detalhes' },
      });
      return;
    }

    // Sem `year`: a consolidação abre no estado inicial, pedindo o ano. Qual ano
    // interessa é decisão do operador, e a listagem não tem como saber.
    void navigate({
      to: '/individual-payment/$financialPersonId/consolidation',
      params: { financialPersonId: String(financialPersonId) },
      search: {},
    });
  };

  const confirmInactivate = () => {
    if (inactivateTarget === null) return;

    inactivate(inactivateTarget.financialPersonId, {
      onSuccess: () => {
        setInactivateTarget(null);
        alert({ alertType: 'success', title: t('actions.deactivateFinancialSuccess') });
      },
    });
  };

  return (
    <IndividualPaymentListTemplate
      filterPanel={
        <IndividualPaymentFilterBar isLoading={isLoading} onSearch={() => setPageIndex(0)} />
      }
      onCreate={() => void navigate({ to: '/individual-payment/create' })}
    >
      <IndividualPaymentTable
        people={people}
        isLoading={isLoading}
        isError={isError}
        hasSearched={hasSearched}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        totalCount={totalCount}
        totalPages={totalPages}
        pageIndex={pageIndex}
        onPageChange={setPageIndex}
        onRetry={() => void refetch()}
        onContractAction={handleContractAction}
      />

      {/* Fica montado o tempo todo e fecha por `action: null`: desmontar um Radix
          Dialog aberto deixa `pointer-events: none` preso no body. */}
      <ParcelActionConfirmModal
        action={inactivateTarget === null ? null : 'inactivateFinancial'}
        header={{
          courseName: inactivateTarget?.context.courseName ?? null,
          studentName: inactivateTarget?.context.personName ?? null,
          ...(inactivateTarget?.context.totalValue == null
            ? {}
            : { totalValue: inactivateTarget.context.totalValue }),
        }}
        isPending={isInactivating}
        error={inactivateError}
        onConfirm={confirmInactivate}
        onClose={() => setInactivateTarget(null)}
      />
    </IndividualPaymentListTemplate>
  );
}
